/**
 * useEasterEggs — Hook for Easter egg state management.
 *
 * DDD co-locatie: Lives in mascot domain alongside easterEggs.ts registry.
 * Manages discovered state via dataLayer, provides trigger functions.
 *
 * Usage:
 *   const { triggerEgg, isDiscovered, discoveredEggs } = useEasterEggs()
 *   if (triggerEgg('rapid-tap'))
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppStore } from '../app/hooks/useAppStore';
import { dataLayer } from '../data/dataLayer';
import { soundManager } from '../utils/soundManager';
import { hapticManager } from '../utils/hapticManager';
import { EASTER_EGGS, type EasterEggId, type EasterEggReaction } from './easterEggs';

// ── Types ────────────────────────────────────────────────────

interface EasterEggTriggerResult {
  triggered: boolean;
  reaction: EasterEggReaction | null;
}
interface UseEasterEggsReturn {
  /** Try to trigger an Easter egg. Returns reaction if successful. */
  triggerEgg: (id: EasterEggId) => EasterEggTriggerResult;
  /** Check if an egg has been discovered before */
  isDiscovered: (id: EasterEggId) => boolean;
  /** Set of all discovered egg IDs */
  discoveredEggs: Set<EasterEggId>;
  /** Total number of discovered eggs */
  discoveredCount: number;
}

// ── Persistence helpers ──────────────────────────────────────

const STORAGE_PREFIX = 'meester-djay-egg-';
function loadDiscoveredEggs(accountId: string | null): Set<EasterEggId> {
  try {
    const key = accountId ? `${STORAGE_PREFIX}${accountId}` : `${STORAGE_PREFIX}global`;
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as EasterEggId[];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}
function saveDiscoveredEggs(accountId: string | null, eggs: Set<EasterEggId>): void {
  try {
    const key = accountId ? `${STORAGE_PREFIX}${accountId}` : `${STORAGE_PREFIX}global`;
    localStorage.setItem(key, JSON.stringify([...eggs]));
  } catch {

    // silent
  }
}

// ── Hook ─────────────────────────────────────────────────────

export function useEasterEggs(): UseEasterEggsReturn {
  const {
    activeAccount
  } = useAppStore();
  const accountId = activeAccount?.id ?? null;
  const [discoveredEggs, setDiscoveredEggs] = useState<Set<EasterEggId>>(new Set());
  const accountIdRef = useRef(accountId);

  // Load on mount and account switch
  useEffect(() => {
    accountIdRef.current = accountId;
    const eggs = loadDiscoveredEggs(accountId);
    if (accountIdRef.current === accountId) {
      setDiscoveredEggs(eggs);
    }
  }, [accountId]);
  const isDiscovered = useCallback((id: EasterEggId) => discoveredEggs.has(id), [discoveredEggs]);
  const triggerEgg = useCallback((id: EasterEggId): EasterEggTriggerResult => {
    const egg = EASTER_EGGS[id];
    if (!egg) return {
      triggered: false,
      reaction: null
    };

    // Non-repeatable eggs that are already discovered → skip
    if (!egg.repeatable && discoveredEggs.has(id)) {
      return {
        triggered: false,
        reaction: null
      };
    }

    // Mark as discovered
    if (!discoveredEggs.has(id)) {
      const updated = new Set(discoveredEggs);
      updated.add(id);
      setDiscoveredEggs(updated);
      saveDiscoveredEggs(accountId, updated);
    }

    // Play sound (type-safe dispatch)
    if (egg.sound) {
      const play = soundManager[egg.sound] as (() => void) | undefined;
      if (play) play();
    }

    // Haptic celebration
    hapticManager.hapticCelebration();
    return {
      triggered: true,
      reaction: egg.reaction
    };
  }, [discoveredEggs, accountId]);
  return {
    triggerEgg,
    isDiscovered,
    discoveredEggs,
    discoveredCount: discoveredEggs.size
  };
}